import { Link, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { SearchTrigger } from "@/components/dex/SearchTrigger";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

type NavItem = { label: string; href: string };
type NavGroup = { label: string; items?: NavItem[]; href?: string };

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Provider Type",
    items: [
      { label: "Physician", href: "/individual" },
      { label: "Nurse Practitioner", href: "/individual/nurse-practitioner" },
      { label: "Physician Assistant", href: "/individual/physician-associate" },
      { label: "Podiatrist", href: "/individual/podiatrist" },
    ],
  },
  {
    label: "Type of Practice",
    items: [
      { label: "Primary Care", href: "/groups" },
      { label: "Urgent Care", href: "/groups" },
      { label: "PACE Organization", href: "/groups/pace" },
      { label: "PACE Directory by State", href: "/pace-organizations" },
      { label: "Direct Primary Care", href: "/individual/direct-primary-care" },
    ],
  },
  {
    label: "Hospitals",
    items: [
      { label: "Curbside & eConsult", href: "/hospital-curbside-econsult" },
      { label: "Tele-Case Management", href: "/tele-case-management" },
      { label: "Tele-Utilization Management", href: "/inpatient-revenue-framework/tele-utilization-management" },
      { label: "Tele-Respiratory Therapy", href: "/tele-respiratory-therapy" },
      { label: "Tele-Hospitalist", href: "/tele-hospitalist" },
      { label: "Tele-Stroke", href: "/tele-stroke" },
      { label: "Inpatient Revenue Framework", href: "/inpatient-revenue-framework" },
      { label: "Critical Access Hospital Directory", href: "/critical-access-hospitals" },
      { label: "REH vs CAH Assessment", href: "/reh-vs-cah" },

      { label: "All Tele Clinical Services", href: "/inpatient-clinical-services" },
    ],
  },
  {
    label: "Clinical Services",
    items: [
      { label: "Tele-Cardiology", href: "/tele-cardiology" },
      { label: "Tele-Endocrinology", href: "/tele-endocrinology" },
      { label: "Tele-Neurology", href: "/tele-neurology" },
      { label: "Tele-Pulmonology", href: "/tele-pulmonology" },
      { label: "Tele-Nephrology", href: "/tele-nephrology" },
      { label: "Tele-Hematology", href: "/tele-hematology" },
      { label: "Tele-Rheumatology", href: "/tele-rheumatology" },
      { label: "Tele-Dermatology", href: "/dermatology" },
      { label: "Cardiology Reads", href: "/cardiology-reads" },
      { label: "PFT Reads", href: "/pulmonary-function-test-reads" },
      { label: "Tele-RT Partnerships (Outpatient)", href: "/tele-respiratory-therapy-partners" },
    ],
  },

  {
    label: "RHTP Grants",
    items: [
      { label: "RHTP Main Page", href: "/rural-health-transformation-program-rhtp" },
      { label: "RFP Radar", href: "/radar" },
      { label: "About Radar", href: "/radar/about" },
    ],
  },
  {
    label: "Careers",
    href: "/careers",
  },
  {
    label: "More",
    items: [
      { label: "Tools & Calculators", href: "/tools" },
      { label: "Resources & Articles", href: "/resources" },
      { label: "PACE Resource Hub", href: "/resources/pace" },
      { label: "PACE Directory Map", href: "/pace-organizations" },
      { label: "Glossary (What Is…)", href: "/what-is" },
    ],
  },
];


const linkClass = (active: boolean) =>
  cn(
    "block rounded-lg px-3 py-2 font-heading text-[0.9rem] font-medium tracking-[-0.005em] transition-all duration-200",
    active
      ? "bg-primary/10 text-primary"
      : "text-foreground/85 hover:bg-primary/5 hover:text-primary",
  );

const triggerClass = (active: boolean, onDark = false) =>
  cn(
    "group relative inline-flex items-center gap-1 rounded-md px-2.5 py-2 font-heading text-[0.9rem] font-semibold tracking-[-0.01em] transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
    onDark
      ? active
        ? "text-primary-foreground"
        : "text-primary-foreground/80 hover:text-primary-foreground"
      : active
        ? "text-primary"
        : "text-foreground/85 hover:text-primary",
  );

const underline = (active: boolean, onDark = false) =>
  cn(
    "pointer-events-none absolute left-2.5 right-2.5 bottom-1 h-px origin-left scale-x-0 transition-transform duration-300 group-hover:scale-x-100 group-data-[state=open]:scale-x-100",
    onDark ? "bg-primary-foreground" : "bg-primary",
    active && "scale-x-100",
  );

export function SolutionsMenu({ className, onDark = false }: { className?: string; onDark?: boolean }) {
  const { pathname } = useLocation();

  return (
    <div className={cn("hidden md:flex items-center gap-0.5 lg:gap-1", className)}>

      {NAV_GROUPS.map((group) => {
        if (group.href && !group.items) {
          const active = pathname === group.href;
          return (
            <Link
              key={group.label}
              to={group.href}
              onClick={() => {
                if (active) window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={triggerClass(active, onDark)}
            >
              {group.label}
              <span className={underline(active, onDark)} />
            </Link>
          );
        }
        const groupActive = group.items?.some((i) => i.href === pathname) ?? false;
        return (
          <DropdownMenu key={group.label}>
            <DropdownMenuTrigger className={triggerClass(groupActive, onDark)}>
              {group.label}
              <ChevronDown className="h-3 w-3 opacity-60 transition-transform duration-200 group-data-[state=open]:rotate-180 group-data-[state=open]:opacity-100" />
              <span className={underline(groupActive, onDark)} />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={12}
              className="w-[264px] rounded-2xl border border-border/50 bg-popover/95 p-1.5 shadow-2xl backdrop-blur-md"
            >
              <ul className="space-y-0.5">
                {group.items?.map((item) => (
                  <li key={`${group.label}-${item.label}`}>
                    <Link
                      to={item.href}
                      onClick={() => {
                        if (pathname === item.href) {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }
                      }}
                      className={linkClass(pathname === item.href)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
      <SearchTrigger className={onDark ? "text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground" : "text-foreground"} />
    </div>
  );
}

export function SolutionsMenuMobile({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();

  return (
    <Accordion type="multiple" className="w-full">
      <SearchTrigger label className="w-full justify-start px-0" onOpen={onNavigate} />
      {NAV_GROUPS.map((group) => {
        if (group.href && !group.items) {
          return (
            <div key={group.label} className="border-b border-border/20">
              <Link
                to={group.href}
                onClick={onNavigate}
                className={cn(
                  "block py-3 text-left font-heading text-sm font-semibold",
                  pathname === group.href ? "text-primary" : "text-foreground/85",
                )}
              >
                {group.label}
              </Link>
            </div>
          );
        }
        return (
          <AccordionItem key={group.label} value={group.label} className="border-b border-border/20">
            <AccordionTrigger className="py-3 text-left font-heading text-sm font-semibold text-foreground/85 hover:no-underline">
              {group.label}
            </AccordionTrigger>
            <AccordionContent className="pb-2">
              <ul className="space-y-1">
                {group.items?.map((item) => (
                  <li key={`${group.label}-${item.label}`}>
                    <Link
                      to={item.href}
                      onClick={onNavigate}
                      className={linkClass(pathname === item.href)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}